import React from 'react';
import { Card, Checkbox, Divider, Empty, Tag, Typography } from 'antd';
import {
	KittingJawCard,
	KittingOtherTag,
	KittingTagsData,
	KittingToolCard,
} from '../../../../redux/api/order-line-item';
import { KittingTagGroupSelection, KittingTagSelection } from './useKittingTagSelection';

const { Text } = Typography;

const selectedCardStyle = {
	border: '2px solid #1890ff',
	background: '#e6f7ff',
	cursor: 'pointer' as const,
	transition: 'all 0.15s',
};

const defaultCardStyle = {
	border: '1px solid #d9d9d9',
	cursor: 'pointer' as const,
	transition: 'all 0.15s',
};

const cardBodyStyle = { body: { padding: '10px 14px' } };

const twoColumnGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 };
const fullWidthGridStyle = { display: 'grid', gridTemplateColumns: '1fr', gap: 6 };

interface IProps {
	data: KittingTagsData | null | undefined;
	selection: KittingTagSelection;
	/** two-column groups Special under Tools and Other under Jaws, which fits the
	 *  floor modal; three-column gives Other and Special tags their own column. */
	layout?: 'two-column' | 'three-column';
	/** Replaces "No tool tags" / "No jaw tags", e.g. to say why they are empty. */
	toolJawEmptyDescription?: string;
}

type HeaderGroup = Pick<KittingTagGroupSelection<unknown>, 'allSelected' | 'toggleAll'> & {
	selected: { size: number };
};

const SectionHeader: React.FC<{ label: string; count: number; group: HeaderGroup }> = ({
	label,
	count,
	group,
}) => (
	<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 44 }}>
		<Text strong style={{ fontSize: 14, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5 }}>
			{label} <Tag style={{ marginLeft: 4 }}>{count}</Tag>
		</Text>
		{count > 0 && (
			<div
				onClick={(e) => {
					e.stopPropagation();
					group.toggleAll();
				}}
				style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, minWidth: 44, paddingLeft: 8, cursor: 'pointer' }}
			>
				<Checkbox
					checked={group.allSelected}
					indeterminate={group.selected.size > 0 && !group.allSelected}
					aria-label={`Select all ${label.toLowerCase()}`}
				/>
				<Text style={{ fontSize: 14 }}>All</Text>
			</div>
		)}
	</div>
);

/** "flat end mill" -> "Flat End Mill". Leaves the rest of each word alone so
 *  acronyms survive. */
const capitalizeWords = (value: string) => value.replace(/\b\w/g, (letter) => letter.toUpperCase());

const SelectableCard: React.FC<{
	checked: boolean;
	onToggle: () => void;
	title: string;
	subtitle?: string;
	children: React.ReactNode;
}> = ({ checked, onToggle, title, subtitle, children }) => (
	<Card
		size="small"
		onClick={onToggle}
		style={checked ? selectedCardStyle : defaultCardStyle}
		styles={cardBodyStyle}
	>
		<div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 28, minWidth: 0 }}>
			<Checkbox checked={checked} aria-label={subtitle ? `${title} ${subtitle}` : title} />
			{/* One shared line height so the 18px number and 14px type centre on the same line. */}
			<span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, lineHeight: '28px' }}>
				<Text strong style={{ fontSize: 18, lineHeight: 'inherit', flexShrink: 0 }}>{title}</Text>
				{subtitle && (
					<>
						<Text type="secondary" style={{ fontSize: 18, lineHeight: 'inherit' }} aria-hidden>
							•
						</Text>
						<Text ellipsis={{ tooltip: subtitle }} style={{ fontSize: 14, lineHeight: 'inherit', minWidth: 0 }}>
							{subtitle}
						</Text>
					</>
				)}
			</span>
		</div>
		<div style={{ marginLeft: 34, marginTop: 2, lineHeight: '18px' }}>{children}</div>
	</Card>
);

type TagGroupProps = {
	tags: KittingOtherTag[];
	group: KittingTagGroupSelection<string>;
	gridStyle: React.CSSProperties;
};

const TagCards: React.FC<TagGroupProps> = ({ tags, group, gridStyle }) => (
	<div style={gridStyle}>
		{tags.map((tag) => (
			<SelectableCard
				key={tag.key}
				checked={group.selected.has(tag.key)}
				onToggle={() => group.toggle(tag.key)}
				title={tag.label}
			>
				<Text type="secondary" style={{ fontSize: 12 }}>{tag.description}</Text>
			</SelectableCard>
		))}
	</div>
);

const TagGroup: React.FC<TagGroupProps & { label: string }> = ({ label, tags, group, gridStyle }) => {
	if (tags.length === 0) return null;
	return (
		<>
			<Divider style={{ margin: '12px 0 4px' }} />
			<SectionHeader label={label} count={tags.length} group={group} />
			<div style={{ marginTop: 4 }}>
				<TagCards tags={tags} group={group} gridStyle={gridStyle} />
			</div>
		</>
	);
};

const columnStyle: React.CSSProperties = { flex: 1, minWidth: 0 };

// Three-column layout: the picker has a fixed height and each column scrolls
// its own cards under a header that stays put.
const PICKER_HEIGHT = 520;
const scrollingColumnStyle: React.CSSProperties = {
	...columnStyle,
	display: 'flex',
	flexDirection: 'column',
	minHeight: 0,
};
const scrollingBodyStyle: React.CSSProperties = {
	flex: '1 1 auto',
	minHeight: 0,
	overflowY: 'auto',
	overflowX: 'clip',
	paddingRight: 4,
};

const columnDivider = <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: 0 }} />;

const emptyGroup = (description: string, image: React.ReactNode) => (
	<Empty description={description} image={image} style={{ margin: '16px 0' }} />
);

const KittingTagPicker: React.FC<IProps> = ({ data, selection, layout = 'two-column', toolJawEmptyDescription }) => {
	const { tools, jaws, other, special } = selection;
	const otherTags = data?.other_tags ?? [];
	const specialTags = data?.special_tags ?? [];
	const gridStyle = layout === 'three-column' ? fullWidthGridStyle : twoColumnGridStyle;
	// The details page matches its other sections' empty states; the floor
	// modal keeps the compact image.
	const emptyImage = layout === 'three-column' ? Empty.PRESENTED_IMAGE_DEFAULT : Empty.PRESENTED_IMAGE_SIMPLE;

	const scrolls = layout === 'three-column';
	const renderColumn = (header: React.ReactNode, body: React.ReactNode) => (
		<div style={scrolls ? scrollingColumnStyle : columnStyle}>
			{header}
			<Divider style={{ margin: '4px 0 6px' }} />
			<div style={scrolls ? scrollingBodyStyle : undefined}>{body}</div>
		</div>
	);

	const toolsColumn = renderColumn(
		<SectionHeader label="Tool Tags" count={data?.tools.length ?? 0} group={tools} />,
		<>
			{data?.tools.length === 0 && emptyGroup(toolJawEmptyDescription ?? 'No tool tags', emptyImage)}
			<div style={gridStyle}>
				{data?.tools.map((tool: KittingToolCard) => (
					<SelectableCard
						key={tool.index}
						checked={tools.selected.has(tool.index)}
						onToggle={() => tools.toggle(tool.index)}
						title={`T${tool.tool_number}`}
						subtitle={tool.type ? capitalizeWords(tool.type) : undefined}
					>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{tool.vendor || '—'} · {tool.product_id || '—'}
						</Text>
						<br />
						<Text type="secondary" style={{ fontSize: 12 }}>
							H: {tool.holder_vendor || '—'} · {tool.holder_product_id || '—'}
						</Text>
					</SelectableCard>
				))}
			</div>
			{layout === 'two-column' && (
				<TagGroup label="Special Tags" tags={specialTags} group={special} gridStyle={gridStyle} />
			)}
		</>
	);

	const jawsColumn = renderColumn(
		<SectionHeader label="Jaw Tags" count={data?.jaws.length ?? 0} group={jaws} />,
		<>
			{data?.jaws.length === 0 && emptyGroup(toolJawEmptyDescription ?? 'No jaw tags', emptyImage)}
			<div style={gridStyle}>
				{data?.jaws.map((jaw: KittingJawCard) => (
					<SelectableCard
						key={jaw.index}
						checked={jaws.selected.has(jaw.index)}
						onToggle={() => jaws.toggle(jaw.index)}
						title={`Setup ${jaw.cam_setup}`}
					>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{jaw.jaw_assembly_name} · Base: {jaw.base_jaws_name}
						</Text>
						<br />
						<Text type="secondary" style={{ fontSize: 12 }}>
							Vise: {jaw.vise}
						</Text>
					</SelectableCard>
				))}
			</div>
			{layout === 'two-column' && (
				<TagGroup label="Other Tags" tags={otherTags} group={other} gridStyle={gridStyle} />
			)}
		</>
	);

	if (layout === 'two-column') {
		return (
			<div style={{ display: 'flex', gap: 16 }}>
				{toolsColumn}
				{columnDivider}
				{jawsColumn}
			</div>
		);
	}

	const otherColumn = renderColumn(
		<SectionHeader label="Other Tags" count={otherTags.length} group={other} />,
		<>
			{otherTags.length === 0 && specialTags.length === 0 && emptyGroup('No other tags', emptyImage)}
			<TagCards tags={otherTags} group={other} gridStyle={gridStyle} />
			<TagGroup label="Special Tags" tags={specialTags} group={special} gridStyle={gridStyle} />
		</>
	);

	return (
		<div style={{ display: 'flex', gap: 16, height: PICKER_HEIGHT }}>
			{toolsColumn}
			{columnDivider}
			{jawsColumn}
			{columnDivider}
			{otherColumn}
		</div>
	);
};

export default KittingTagPicker;
